import React from 'react';
import {
  AbsoluteFill,
  spring,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
} from 'remotion';

// CSS Houdini Paint API 模拟
export const CSSHoudiniPaintAPI: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // Spring 动画
  const entranceProgress = spring({
    frame,
    fps,
    config: {
      damping: 100,
      stiffness: 200,
      mass: 0.5,
    },
  });

  // 绘制波纹渐变（模拟 CSS Paint Worklet）
  const RippleGradient = ({ x, y, width, height }: { x: number; y: number; width: number; height: number }) => {
    const centerX = width / 2;
    const centerY = height / 2;
    const maxRadius = Math.sqrt(centerX * centerX + centerY * centerY);
    const rings = 8;

    return (
      <svg x={x} y={y} width={width} height={height}>
        <defs>
          <radialGradient id={`ripple-${x}-${y}`}>
            {Array.from({ length: rings }, (_, i) => {
              const offset = (i / rings + frame * 0.01) % 1;
              const hue = (i * 45 + frame * 2) % 360;
              return (
                <stop
                  key={i}
                  offset={offset}
                  stopColor={`hsl(${hue}, 70%, 60%)`}
                  stopOpacity={0.8}
                />
              );
            })}
          </radialGradient>
        </defs>
        <rect
          width={width}
          height={height}
          fill={`url(#ripple-${x}-${y})`}
        />
      </svg>
    );
  };

  // 绘制网格背景（模拟 CSS Paint Worklet）
  const GridBackground = ({ x, y, width, height }: { x: number; y: number; width: number; height: number }) => {
    const gridSize = 30;
    const lines = [];

    // 垂直线
    for (let i = 0; i <= width / gridSize; i++) {
      const offset = (frame * 0.5 + i * 10) % 50;
      lines.push(
        <line
          key={`v-${i}`}
          x1={i * gridSize}
          y1={0}
          x2={i * gridSize}
          y2={height}
          stroke={`hsl(${200 + offset * 2}, 70%, 50%)`}
          strokeWidth={1}
          opacity={0.5}
        />
      );
    }

    // 水平线
    for (let i = 0; i <= height / gridSize; i++) {
      const offset = (frame * 0.5 + i * 10) % 50;
      lines.push(
        <line
          key={`h-${i}`}
          x1={0}
          y1={i * gridSize}
          x2={width}
          y2={i * gridSize}
          stroke={`hsl(${200 + offset * 2}, 70%, 50%)`}
          strokeWidth={1}
          opacity={0.5}
        />
      );
    }

    return (
      <svg x={x} y={y} width={width} height={height}>
        {lines}
      </svg>
    );
  };

  // 绘制噪点纹理（模拟 CSS Paint Worklet）
  const NoiseTexture = ({ x, y, width, height }: { x: number; y: number; width: number; height: number }) => {
    const dots = [];
    const density = 50;
    const seed = 12345;

    // 简化的 Perlin 噪声模拟
    const noise = (x: number, y: number) => {
      const n = Math.sin(x * 0.1 + frame * 0.05) * Math.cos(y * 0.1 + frame * 0.05);
      return (Math.sin(n * seed) + 1) / 2;
    };

    for (let i = 0; i < density; i++) {
      for (let j = 0; j < density; j++) {
        const px = (i / density) * width;
        const py = (j / density) * height;
        const opacity = noise(i, j);
        const size = 2 + noise(i + 100, j + 100) * 3;

        dots.push(
          <circle
            key={`${i}-${j}`}
            cx={px}
            cy={py}
            r={size}
            fill={`hsl(${280 + noise(i + 200, j + 200) * 60}, 70%, 60%)`}
            opacity={opacity * 0.8}
          />
        );
      }
    }

    return (
      <svg x={x} y={y} width={width} height={height}>
        {dots}
      </svg>
    );
  };

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0f0f23',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: 'Arial, sans-serif',
          fontSize: 48,
          fontWeight: 'bold',
          color: '#00d4ff',
          opacity: entranceProgress,
          transform: `translateY(${interpolate(entranceProgress, [0, 1], [-50, 0])}px)`,
        }}
      >
        CSS Houdini Paint API
      </div>

      {/* 三个自定义绘制效果 */}
      <div
        style={{
          display: 'flex',
          gap: 40,
          marginTop: -40,
          opacity: entranceProgress,
          transform: `translateY(${interpolate(entranceProgress, [0, 1], [100, 0])}px)`,
        }}
      >
        {/* 波纹渐变 */}
        <div style={{ textAlign: 'center' }}>
          <svg width={300} height={300} style={{ borderRadius: 10, overflow: 'hidden' }}>
            <rect width={300} height={300} fill="#1a1a3e" />
            <RippleGradient x={0} y={0} width={300} height={300} />
          </svg>
          <div
            style={{
              marginTop: 15,
              fontFamily: 'Arial, sans-serif',
              fontSize: 26,
              color: '#fff',
            }}
          >
            Ripple Gradient
          </div>
          <div
            style={{
              marginTop: 5,
              fontFamily: 'monospace',
              fontSize: 22,
              color: '#888',
            }}
          >
            background: paint(ripple-gradient)
          </div>
        </div>

        {/* 网格背景 */}
        <div style={{ textAlign: 'center' }}>
          <svg width={300} height={300} style={{ borderRadius: 10, overflow: 'hidden' }}>
            <rect width={300} height={300} fill="#1a1a3e" />
            <GridBackground x={0} y={0} width={300} height={300} />
          </svg>
          <div
            style={{
              marginTop: 15,
              fontFamily: 'Arial, sans-serif',
              fontSize: 26,
              color: '#fff',
            }}
          >
            Grid Background
          </div>
          <div
            style={{
              marginTop: 5,
              fontFamily: 'monospace',
              fontSize: 22,
              color: '#888',
            }}
          >
            background: paint(grid-background)
          </div>
        </div>

        {/* 噪点纹理 */}
        <div style={{ textAlign: 'center' }}>
          <svg width={300} height={300} style={{ borderRadius: 10, overflow: 'hidden' }}>
            <rect width={300} height={300} fill="#1a1a3e" />
            <NoiseTexture x={0} y={0} width={300} height={300} />
          </svg>
          <div
            style={{
              marginTop: 15,
              fontFamily: 'Arial, sans-serif',
              fontSize: 26,
              color: '#fff',
            }}
          >
            Noise Texture
          </div>
          <div
            style={{
              marginTop: 5,
              fontFamily: 'monospace',
              fontSize: 22,
              color: '#888',
            }}
          >
            background: paint(noise-texture)
          </div>
        </div>
      </div>

      {/* Houdini API 架构说明 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 40,
          right: 40,
          display: 'flex',
          justifyContent: 'space-between',
          opacity: entranceProgress,
        }}
      >
        {/* Worklet 生命周期 */}
        <div
          style={{
            backgroundColor: 'rgba(0, 212, 255, 0.1)',
            padding: 20,
            borderRadius: 10,
            border: '1px solid rgba(0, 212, 255, 0.3)',
          }}
        >
          <div style={{ color: '#00d4ff', fontSize: 26, marginBottom: 10 }}>
            🎨 Paint Worklet Lifecycle
          </div>
          <div style={{ color: '#fff', fontSize: 24, lineHeight: 1.6 }}>
            1. registerPaint('name', class)
          </div>
          <div style={{ color: '#fff', fontSize: 24, lineHeight: 1.6 }}>
            2. inputProperties: ['--custom-prop']
          </div>
          <div style={{ color: '#fff', fontSize: 24, lineHeight: 1.6 }}>
            3. paint(ctx, size, props)
          </div>
        </div>

        {/* CSS 变量驱动 */}
        <div
          style={{
            backgroundColor: 'rgba(255, 136, 0, 0.1)',
            padding: 20,
            borderRadius: 10,
            border: '1px solid rgba(255, 136, 0, 0.3)',
          }}
        >
          <div style={{ color: '#ff8800', fontSize: 26, marginBottom: 10 }}>
            ⚙️ CSS Variables
          </div>
          <div style={{ color: '#888', fontSize: 22, lineHeight: 1.6 }}>
            --ripple-color: hsl(200, 70%, 60%)
          </div>
          <div style={{ color: '#888', fontSize: 22, lineHeight: 1.6 }}>
            --grid-size: 30px
          </div>
          <div style={{ color: '#888', fontSize: 22, lineHeight: 1.6 }}>
            --noise-density: 50
          </div>
        </div>

        {/* 性能优势 */}
        <div
          style={{
            backgroundColor: 'rgba(0, 255, 136, 0.1)',
            padding: 20,
            borderRadius: 10,
            border: '1px solid rgba(0, 255, 136, 0.3)',
          }}
        >
          <div style={{ color: '#0f8', fontSize: 26, marginBottom: 10 }}>
            🚀 Performance
          </div>
          <div style={{ color: '#888', fontSize: 22, lineHeight: 1.6 }}>
            • Runs in separate thread
          </div>
          <div style={{ color: '#888', fontSize: 22, lineHeight: 1.6 }}>
            • GPU accelerated
          </div>
          <div style={{ color: '#888', fontSize: 22, lineHeight: 1.6 }}>
            • No main thread blocking
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
